import { useState, type ReactNode } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { AlertTriangle, BellRing, CalendarDays, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { Avisos } from '../api/agenda';

// Un correo de aviso del CRM (solo admin): encendido o apagado, a quién va,
// «Enviar una prueba» y el resultado del último envío. Lo usan la Agenda (cada
// mañana) y Tu web (los lunes). Arranca apagado: nadie recibe nada hasta que
// alguien lo enciende.

const INPUT =
  'w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep outline-none placeholder:text-ink/40 focus:border-brand';

export default function AvisosCorreo({ titulo, children, interruptor, boton, avisos, guardar, probar }: {
  titulo: string;
  /** Qué llega y cuándo. */
  children: ReactNode;
  /** El texto de la casilla de encender. */
  interruptor: string;
  boton: string;
  avisos: Avisos | undefined;
  guardar: UseMutationResult<Avisos, unknown, Pick<Avisos, 'activo' | 'destinatarios'>>;
  probar: UseMutationResult<unknown, unknown, void>;
}) {
  const [activo, setActivo] = useState<boolean | null>(null);
  const [texto, setTexto] = useState<string | null>(null);

  if (!avisos) return null;
  const activoV = activo ?? avisos.activo;
  const textoV = texto ?? avisos.destinatarios.join(', ');
  const destinatarios = textoV.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  const cambiado = activoV !== avisos.activo || destinatarios.join(',') !== avisos.destinatarios.join(',');
  const error = (guardar.error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;
  const cuando = avisos.ultimo_envio
    ? new Date(Number(avisos.ultimo_envio) * 1000).toLocaleString('es-ES', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <section className="mt-8 rounded-2xl border border-deep/10 bg-surface p-5">
      <h2 className="flex items-center gap-2 text-base font-extrabold text-deep">
        <BellRing size={18} className="text-brand" /> {titulo}
      </h2>
      <p className="mt-1 text-sm text-ink/60">{children}</p>
      <label className="mt-4 flex items-center gap-3 text-sm font-bold text-deep">
        <input type="checkbox" checked={activoV} onChange={(e) => setActivo(e.target.checked)} className="h-4 w-4 accent-brand" />
        {interruptor}
      </label>
      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-bold text-ink/50">Correos (separados por comas, hasta 5)</span>
        <input value={textoV} onChange={(e) => setTexto(e.target.value)} placeholder="gerencia@habitania.info" className={INPUT} />
      </label>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => guardar.mutate({ activo: activoV, destinatarios }, { onSuccess: () => { setActivo(null); setTexto(null); } })}
          disabled={!cambiado || guardar.isPending}
          className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-deep disabled:opacity-40"
        >
          {boton}
        </button>
        <button
          onClick={() => probar.mutate()}
          disabled={cambiado || probar.isPending || avisos.destinatarios.length === 0}
          title={cambiado ? 'Guarda antes los cambios' : undefined}
          className="flex items-center gap-2 rounded-full border border-deep/15 px-4 py-2 text-sm font-bold text-deep hover:border-brand hover:text-brand disabled:opacity-40"
        >
          {probar.isPending && <Loader2 size={14} className="animate-spin" />}
          Enviar una prueba
        </button>
      </div>
      {avisos.ultimo_resultado && (
        <p className={clsx('mt-4 flex items-start gap-2 rounded-xl px-3 py-2 text-sm', avisos.ultimo_ok ? 'bg-leaf/30 text-deep' : 'bg-red-50 text-red-700')}>
          {avisos.ultimo_ok ? <Check size={16} className="mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          <span>Último envío{cuando ? `, ${cuando}` : ''}: {avisos.ultimo_resultado}</span>
        </p>
      )}
      {avisos.ses_produccion === false && (
        <p className="mt-3 flex items-center gap-2 text-xs text-ink/45">
          <CalendarDays size={13} /> Por ahora los correos solo llegan a direcciones @habitania.info:
          el envío a cualquier otra está pendiente de que AWS lo autorice.
        </p>
      )}
    </section>
  );
}
